import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { stockService } from '../services/stockService';
import './TradeRecap.css';

/**
 * Trade Recap Product Search Component
 * 
 * Features:
 * - Debounced search (300ms)
 * - Connects to stockService.searchVariantsForInvoice()
 * - Stores stock_item_variant ID
 * - Displays product name/description in input
 * - Matches TradeRecap form styling
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.fieldName - Field name for the product ID (stock_item_variant)
 * @param {string} props.displayFieldName - Field name for the display text (product)
 * @param {string} props.value - Current product display value
 * @param {string} props.variantId - Current stock_item_variant ID
 * @param {Function} props.onChange - Callback with (variantId, displayName)
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Function} props.filterFunction - Optional filter function to apply to search results
 * @param {Function} props.onCreateProduct - Sprint D: optional callback. If provided,
 *   the empty-state dropdown shows a "+ Create product" button that calls
 *   onCreateProduct(searchTerm). The caller owns the InlineProductCreator orchestration
 *   and decides what to do with the new variant (e.g. attach to a recap field).
 */
const RecapProductSearch = ({ 
  label = 'Product',
  fieldName = 'stock_item_variant',
  displayFieldName = 'product',
  value = '', 
  variantId = null,
  onChange,
  disabled = false,
  filterFunction = null,
  onCreateProduct = null,
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isProductSelected, setIsProductSelected] = useState(!!value);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const isLocalActionRef = useRef(false);
  const localActionTimerRef = useRef(null);

  // Update search term when value changes externally
  useEffect(() => {
    // Skip sync if we just performed a local action (clear or select)
    if (isLocalActionRef.current) {
      return;
    }
    
    if (value && value !== searchTerm) {
      setSearchTerm(value);
      setIsProductSelected(true);
    } else if (!value && searchTerm) {
      setSearchTerm('');
      setIsProductSelected(false);
    }
  }, [value]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (localActionTimerRef.current) {
        clearTimeout(localActionTimerRef.current);
      }
    };
  }, []);

  // Load product details when editing existing recap
  useEffect(() => {
    // Skip if we just performed a local action (clear or select)
    if (isLocalActionRef.current) {
      return;
    }
    
    // Only load if variantId exists and value is empty
    // But don't load if searchTerm is already empty (indicates user cleared it)
    if (variantId && !value && searchTerm !== '') {
      const loadProductDetails = async () => {
        try {
          const variant = await stockService.getVariantById(variantId);
          if (variant) {
            let cleanDescription = '';
            if (variant.display_name) {
              cleanDescription = variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
            } else {
              // Reconstruct from variant properties
              // Note: StockVariantForInvoiceSerializer returns FLATTENED fields directly on variant
              const productType = variant.product_type_display || '';
              const country = variant.country_of_origin || '';
              const supplier = variant.supplier_name || '';
              const shortname = variant.shortname || '';
              
              if (productType && country && supplier && shortname) {
                cleanDescription = `${productType} | ${country} | ${supplier} - ${shortname}`;
              } else {
                cleanDescription = shortname || variant.full_description || 'Product';
              }
            }
            
            setSearchTerm(cleanDescription);
            setIsProductSelected(true);
          }
        } catch (error) {
          console.error('Error loading product details:', error);
        }
      };
      loadProductDetails();
    } else if (!variantId && !value && searchTerm) {
      // If both variantId and value are cleared, clear the search term
      setSearchTerm('');
      setIsProductSelected(false);
    }
  }, [variantId, value]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch products when debounced search changes
  useEffect(() => {
    if (debouncedSearch.trim().length >= 2 && !isProductSelected) {
      fetchProducts(debouncedSearch);
    } else {
      setFilteredProducts([]);
    }
  }, [debouncedSearch, isProductSelected]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProducts = async (search) => {
    try {
      setLoading(true);
      const results = await stockService.searchVariantsForInvoice(search);
      
      // Apply filter function if provided
      const filteredResults = filterFunction ? results.filter(filterFunction) : results;
      
      setFilteredProducts(filteredResults);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset keyboard highlight whenever the visible results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [filteredProducts, showDropdown]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      return;
    }
    if (!showDropdown || filteredProducts.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, filteredProducts.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectProduct(filteredProducts[highlightIndex]);
    }
  };

  const handleSelectProduct = (variant) => {
    // Set local action flag to prevent useEffect from syncing
    isLocalActionRef.current = true;
    if (localActionTimerRef.current) clearTimeout(localActionTimerRef.current);
    localActionTimerRef.current = setTimeout(() => {
      isLocalActionRef.current = false;
    }, 500);
    
    // Build display name from variant - match DeliveryForm format exactly
    let displayName = '';
    if (variant.display_name) {
      displayName = variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
    } else {
      // Reconstruct from variant properties
      // Note: StockVariantForInvoiceSerializer returns FLATTENED fields directly on variant
      const productType = variant.product_type_display || '';
      const country = variant.country_of_origin || '';
      const supplier = variant.supplier_name || '';
      const shortname = variant.shortname || '';
      
      if (productType && country && supplier && shortname) {
        displayName = `${productType} | ${country} | ${supplier} - ${shortname}`;
      } else {
        displayName = shortname || variant.full_description || 'Product';
      }
    }

    setSearchTerm(displayName);
    setShowDropdown(false);
    setIsProductSelected(true);
    
    // Call onChange with variant ID, display name, and full variant data
    if (onChange) {
      onChange(variant.id, displayName, variant);
    }
  };

  const handleClear = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Set local action flag to block useEffect from syncing for 500ms
    isLocalActionRef.current = true;
    if (localActionTimerRef.current) clearTimeout(localActionTimerRef.current);
    localActionTimerRef.current = setTimeout(() => {
      isLocalActionRef.current = false;
    }, 500);
    
    // Clear all state immediately
    setSearchTerm('');
    setIsProductSelected(false);
    setFilteredProducts([]);
    setShowDropdown(false);
    
    // CRITICAL: Notify parent component to clear BOTH value AND variantId
    // Otherwise the variantId stays set and triggers the product detail loading effect
    if (onChange) {
      onChange(null, '', null);
    }
  };

  return (
    <div className="trade-recap-form-group" ref={dropdownRef} style={{ position: 'relative' }}>
      {label && (
        <label htmlFor={fieldName} className="trade-recap-label">{label}</label>
      )}
      <div style={{ position: 'relative' }}>
        {!isProductSelected && (
          <FontAwesomeIcon 
            icon={faSearch} 
            style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#666',
              pointerEvents: 'none',
              zIndex: 1
            }} 
          />
        )}
        <input
          type="text"
          id={fieldName}
          name={displayFieldName}
          className="trade-recap-input"
          style={{ 
            paddingLeft: isProductSelected ? '12px' : '36px',
            paddingRight: searchTerm ? '36px' : '12px',
            fontWeight: isProductSelected ? '700' : '400'
          }}
          value={searchTerm}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearchTerm(newValue);
            setShowDropdown(true);
            setIsProductSelected(false);
            
            // If input is cleared, clear the stored field values
            if (newValue === '') {
              if (onChange) {
                onChange(null, '');
              }
            }
          }}
          onFocus={() => {
            if (searchTerm.length >= 2 && !isProductSelected) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search for product..."
          disabled={disabled}
        />
        {/* A disabled box can't be cleared either (it is read-only). */}
        {searchTerm && !disabled && (
          <FontAwesomeIcon 
            icon={faTimes} 
            onClick={handleClear}
            style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#999',
              cursor: 'pointer',
              zIndex: 1
            }} 
          />
        )}
      </div>
      
      {showDropdown && debouncedSearch.trim().length >= 2 && (
        <div className="recap-product-dropdown" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '300px',
          overflowY: 'auto',
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 9999,
          marginTop: '4px'
        }}>
          {loading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
              Searching products...
            </div>
          ) : filteredProducts.length > 0 ? (
            filteredProducts.map((variant, idx) => (
              <div
                key={variant.id}
                ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                onClick={() => handleSelectProduct(variant)}
                className="recap-product-dropdown-item"
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  transition: 'background-color 0.2s ease',
                  backgroundColor: idx === highlightIndex ? '#f5f5f5' : 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx === highlightIndex ? '#f5f5f5' : 'transparent'}
              >
                <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>
                  {variant.display_name}
                  {variant.quantity && variant.unit_name && (
                    <span style={{
                      marginLeft: '8px',
                      fontWeight: '600',
                      color: '#2c5aa0',
                      fontSize: '13px'
                    }}>
                      ({parseFloat(variant.quantity).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} {variant.unit_name})
                    </span>
                  )}
                  {variant.has_movements && variant.unit_name && (
                    <span
                      title="Live stockpile balance from the ledger"
                      style={{
                        marginLeft: '6px',
                        fontWeight: '500',
                        color: '#475569',
                        background: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        padding: '1px 6px',
                        fontSize: '12px',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      On hand: {parseFloat(variant.on_hand_balance || 0)
                        .toLocaleString('en-US', { maximumFractionDigits: 2 })
                        .replace(/,/g, ' ')} {variant.unit_name}
                    </span>
                  )}
                </div>
                {variant.quality && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Quality: {variant.quality.substring(0, 80)}{variant.quality.length > 80 ? '...' : ''}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
              <div style={{ marginBottom: '8px' }}>No products found.</div>
              {onCreateProduct ? (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCreateProduct(debouncedSearch);
                      setShowDropdown(false);
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    + Create product
                  </button>
                  <span style={{ fontSize: '12px', color: '#999' }}>or enter manually above</span>
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#999' }}>You can enter manually.</div>
              )}
            </div>
          )}
        </div>
      )}
      {debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < 2 && !isProductSelected && (
        <span style={{ fontSize: '0.85em', color: '#666', marginTop: '2px', display: 'block' }}>
          Type at least 2 characters to search products
        </span>
      )}
    </div>
  );
};

export default RecapProductSearch;

